import React, { useEffect, useState } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FileText, Plus, Trash2, Save, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin "Pages" Tab — CMS-like control over page content.
 * Each page (home, pricing, about, contact, ...) has a list of editable sections
 * (heading, body, CTA, image, enabled, sort order). Admin can edit text safely
 * without breaking core logic. Backend: /api/admin/pages, /api/admin/pages/{slug}
 */

export default function AdminPagesTab() {
  const [pages, setPages] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [draft, setDraft] = useState(null); // edited copy of currently active page
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/pages");
      setPages(r.data);
      if (!activeSlug && r.data.length) setActiveSlug(r.data[0].slug);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to load pages"));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Whenever activeSlug changes, reset draft from server-pages
  useEffect(() => {
    if (!activeSlug) { setDraft(null); return; }
    const p = pages.find((x) => x.slug === activeSlug);
    if (p) setDraft({ ...p, sections: [...(p.sections || [])] });
  }, [activeSlug, pages]);

  const updateSection = (idx, key, value) => {
    setDraft((d) => {
      const sections = d.sections.map((s, i) => i === idx ? { ...s, [key]: value } : s);
      return { ...d, sections };
    });
  };

  const moveSection = (idx, dir) => {
    setDraft((d) => {
      const sections = [...d.sections];
      const j = idx + dir;
      if (j < 0 || j >= sections.length) return d;
      [sections[idx], sections[j]] = [sections[j], sections[idx]];
      // Re-stamp sort_order
      sections.forEach((s, i) => { s.sort_order = i; });
      return { ...d, sections };
    });
  };

  const removeSection = (idx) => {
    setDraft((d) => ({ ...d, sections: d.sections.filter((_, i) => i !== idx) }));
  };

  const addSection = () => {
    setDraft((d) => ({
      ...d,
      sections: [
        ...(d.sections || []),
        {
          key: `section_${Date.now()}`,
          heading: "New section",
          body: "",
          cta_text: "",
          cta_link: "",
          image_url: "",
          enabled: true,
          sort_order: (d.sections || []).length,
        },
      ],
    }));
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await api.put(`/admin/pages/${draft.slug}`, {
        title: draft.title,
        sections: (draft.sections || []).map((s, i) => ({ ...s, sort_order: i })),
        meta: draft.meta || {},
      });
      toast.success("Page saved");
      await load();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Save failed"));
    }
    setBusy(false);
  };

  if (!pages.length) {
    return <div className="text-[#A1A1AA] text-sm p-6 text-center bg-white border border-dashed border-[#E4E4E7] rounded-sm">Loading pages…</div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-pages-tab">
      <div className="flex items-center gap-2">
        <FileText size={16} className="text-[#0055FF]" />
        <h2 className="font-heading font-bold text-xl tracking-tight">Page Content (CMS)</h2>
        <span className="text-[11px] text-[#A1A1AA]">Edit safely — text only. Core logic untouched.</span>
      </div>

      <Tabs value={activeSlug || ""} onValueChange={setActiveSlug}>
        <TabsList className="bg-white border border-[#E4E4E7] rounded-sm flex-wrap h-auto" data-testid="pages-tablist">
          {pages.map((p) => (
            <TabsTrigger key={p.slug} value={p.slug} className="rounded-sm" data-testid={`page-tab-${p.slug}`}>
              {p.title || p.slug}
            </TabsTrigger>
          ))}
        </TabsList>

        {pages.map((p) => (
          <TabsContent key={p.slug} value={p.slug} className="mt-4">
            {draft && draft.slug === p.slug && (
              <div className="space-y-5">
                {/* Title + meta */}
                <div className="bg-white border border-[#E4E4E7] rounded-sm p-5 space-y-3">
                  <div>
                    <Lbl>Page Title</Lbl>
                    <Input
                      value={draft.title || ""}
                      onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      className="rounded-sm"
                      data-testid={`page-title-${p.slug}`}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Lbl>Meta description (SEO)</Lbl>
                      <Input
                        value={draft.meta?.description || ""}
                        onChange={(e) => setDraft({ ...draft, meta: { ...(draft.meta || {}), description: e.target.value } })}
                        className="rounded-sm"
                      />
                    </div>
                    <div>
                      <Lbl>Meta keywords</Lbl>
                      <Input
                        value={draft.meta?.keywords || ""}
                        onChange={(e) => setDraft({ ...draft, meta: { ...(draft.meta || {}), keywords: e.target.value } })}
                        className="rounded-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Sections list */}
                <div className="space-y-3">
                  {(draft.sections || []).map((s, i) => (
                    <div key={i} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`page-section-${i}`}>
                      <div className="flex items-center justify-between mb-3 gap-2">
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#A1A1AA] font-mono-data flex-1 truncate">
                          {s.key}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <label className="flex items-center gap-1.5 text-[11px] text-[#52525B]">
                            <Switch
                              checked={!!s.enabled}
                              onCheckedChange={(v) => updateSection(i, "enabled", v)}
                              data-testid={`section-enabled-${i}`}
                            /> {s.enabled ? "On" : "Off"}
                          </label>
                          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveSection(i, -1)} disabled={i === 0} data-testid={`section-up-${i}`}>
                            <ArrowUp size={13} />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveSection(i, +1)} disabled={i === draft.sections.length - 1} data-testid={`section-down-${i}`}>
                            <ArrowDown size={13} />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#DC2626]" onClick={() => removeSection(i)} data-testid={`section-del-${i}`}>
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <Lbl>Section key</Lbl>
                          <Input
                            value={s.key}
                            onChange={(e) => updateSection(i, "key", e.target.value)}
                            className="rounded-sm font-mono-data text-[12px]"
                          />
                        </div>
                        <div>
                          <Lbl>Heading</Lbl>
                          <Input
                            value={s.heading || ""}
                            onChange={(e) => updateSection(i, "heading", e.target.value)}
                            className="rounded-sm"
                            data-testid={`section-heading-${i}`}
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Lbl>Body</Lbl>
                        <Textarea
                          rows={3}
                          value={s.body || ""}
                          onChange={(e) => updateSection(i, "body", e.target.value)}
                          className="rounded-sm"
                          data-testid={`section-body-${i}`}
                        />
                      </div>
                      <div className="grid sm:grid-cols-3 gap-3 mt-3">
                        <div>
                          <Lbl>CTA Text</Lbl>
                          <Input
                            value={s.cta_text || ""}
                            onChange={(e) => updateSection(i, "cta_text", e.target.value)}
                            className="rounded-sm"
                          />
                        </div>
                        <div>
                          <Lbl>CTA Link</Lbl>
                          <Input
                            value={s.cta_link || ""}
                            onChange={(e) => updateSection(i, "cta_link", e.target.value)}
                            className="rounded-sm"
                            placeholder="/register"
                          />
                        </div>
                        <div>
                          <Lbl>Image URL</Lbl>
                          <Input
                            value={s.image_url || ""}
                            onChange={(e) => updateSection(i, "image_url", e.target.value)}
                            className="rounded-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="rounded-sm" onClick={addSection} data-testid="add-section-btn">
                    <Plus size={13} className="mr-1.5" /> Add section
                  </Button>
                  <Button type="button" onClick={save} disabled={busy} className="btn-brand rounded-sm" data-testid={`save-page-${p.slug}`}>
                    <Save size={13} className="mr-1.5" /> {busy ? "Saving…" : "Save Page"}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

const Lbl = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">{children}</div>
);
